import { UserButton } from "@clerk/react";
import {
  Boxes,
  Folder,
  Search,
  MessageCircle,
  Terminal as TerminalIcon,
  Bot,
  Shield,
  Settings,
} from "lucide-react";
import { Logo } from "../Logo";
import { routeLinkHandler } from "../../routeNav";
import type { ActivitySurface } from "./types";

interface ActivityButtonProps {
  surface: ActivitySurface;
  active: boolean;
  onSelect: (surface: ActivitySurface) => void;
  title: string;
  badge?: number;
  children: React.ReactNode;
}

function ActivityButton({
  surface,
  active,
  onSelect,
  title,
  badge,
  children,
}: ActivityButtonProps) {
  return (
    <button
      type="button"
      className={`activity-bar__btn${active ? " is-active" : ""}`}
      title={title}
      aria-label={title}
      onClick={() => onSelect(surface)}
    >
      {children}
      {badge !== undefined && badge > 0 && <span className="activity-bar__badge">{badge}</span>}
    </button>
  );
}

interface Props {
  surface: ActivitySurface;
  sessionCollapsed: boolean;
  // Whether the terminal overlay is currently shown. Drives the terminal
  // button's active state (the button toggles overlay visibility).
  terminalActive: boolean;
  sessionsBadge: number;
  hasUpdateBadge: boolean;
  onSurfaceSelect: (surface: ActivitySurface) => void;
  onTerminalMax: () => void;
  onSettingsOpen: () => void;
  onSearchOpen: () => void;
}

export function ActivityBar({
  surface,
  sessionCollapsed,
  terminalActive,
  sessionsBadge,
  hasUpdateBadge,
  onSurfaceSelect,
  onTerminalMax,
  onSettingsOpen,
  onSearchOpen,
}: Props) {
  const isActive = (s: ActivitySurface) => surface === s && !sessionCollapsed;

  return (
    <nav className="activity-bar" aria-label="Workbench surfaces">
      <a
        className="activity-bar__brand"
        href="/"
        aria-label="Steerly Workbench"
        title="Steerly Workbench"
        onClick={routeLinkHandler("/")}
      >
        <Logo />
      </a>
      <ActivityButton
        surface="workspaces"
        active={isActive("workspaces")}
        onSelect={onSurfaceSelect}
        title="Workspaces – switch or manage workbenches"
      >
        <Boxes />
      </ActivityButton>
      <ActivityButton
        surface="explorer"
        active={isActive("explorer")}
        onSelect={onSurfaceSelect}
        title="Explorer - projects and files"
      >
        <Folder />
      </ActivityButton>
      <ActivityButton
        surface="search"
        active={isActive("search")}
        onSelect={() => onSearchOpen()}
        title="Search files and text"
      >
        <Search />
      </ActivityButton>
      <ActivityButton
        surface="sessions"
        active={isActive("sessions")}
        onSelect={onSurfaceSelect}
        title="Sessions"
        badge={sessionsBadge}
      >
        <MessageCircle />
      </ActivityButton>
      <button
        type="button"
        className={`activity-bar__btn${terminalActive ? " is-active" : ""}`}
        title="Terminal – show/hide multi-agent workspace"
        aria-label="Terminal – show/hide multi-agent workspace"
        onClick={onTerminalMax}
      >
        <TerminalIcon />
      </button>
      <ActivityButton
        surface="agents"
        active={isActive("agents")}
        onSelect={onSurfaceSelect}
        title="Agents"
      >
        <Bot />
      </ActivityButton>

      <div className="activity-bar__spacer" />

      <div className="activity-bar__user" aria-label="Account">
        <UserButton appearance={{ elements: { rootBox: { width: "auto" } } }} />
      </div>
      <a
        className="activity-bar__btn"
        href="/security-center"
        title="Security Center"
        onClick={routeLinkHandler("/security-center")}
      >
        <Shield />
      </a>
      <button
        type="button"
        className={`activity-bar__btn${hasUpdateBadge ? " has-update-badge" : ""}`}
        title="Settings"
        aria-label="Open Settings"
        onClick={onSettingsOpen}
      >
        <Settings />
      </button>
    </nav>
  );
}
